import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import MicLargeIcon from '@/assets/icons/mic-large.svg';
import { startSpeechRecogntion, SpeechRecognitionEvent } from '@/shared/helpers/speech-recognition';
import { Loading } from '@/shared/ui/loading/routeLoading.tsx';

interface ActiveVoiceRecognitionProps {
    errorEncountered: (event: { error: string }) => void;
    onClose: (transcript: string) => void;
    onDidnGetThat: () => void;
}

export const ActiveVoiceRecognition = ({ errorEncountered, onClose, onDidnGetThat }: ActiveVoiceRecognitionProps) => {
    const [transcript, setTranscript] = useState('');
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [showPulse, setShowPulse] = useState(false);
    const pulseRef = useRef<HTMLDivElement | null>(null);
    const [initialzed, setInitialized] = useState(false);

    const updateTranscript = (event: SpeechRecognitionEvent) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let ind = event.resultIndex; ind < event.results.length; ++ind) {
            if (event.results[ind].isFinal) {
                finalTranscript += event.results[ind][0].transcript;
            } else {
                interimTranscript += event.results[ind][0].transcript;
            }
        }
        setShowPulse(true);
        setTranscript(interimTranscript || finalTranscript);
    };

    const clearTimers = () => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
        }
        if (inactivityTimeoutRef.current !== null) {
            clearTimeout(inactivityTimeoutRef.current);
        }
    }

    useEffect(() => {
        if (transcript) {
            clearTimers();
            timeoutRef.current = setTimeout(() => onClose(transcript), 1000);
        }
    }, [transcript]);


    const onerror = (event) => {
        clearTimers();
        errorEncountered(event);
    }

    useEffect(() => {
        const { stop, errorMessage } = startSpeechRecogntion(updateTranscript, onerror);

        if (errorMessage) {
            errorEncountered({ error: errorMessage });
        } else {
            inactivityTimeoutRef.current = setTimeout(() => {
                onDidnGetThat();
            }, 7000)
        }

        return () => {
            stop?.();
            clearTimers();
        };
    }, []);

    useEffect(() => {
        setTimeout(() => {
            setInitialized(true);
        }, 600);
    }, [])

    useLayoutEffect(() => {
        let pulseTimeout: ReturnType<typeof setTimeout> | null = null;

        const randomizePulsation = () => {
            const scale = .8 + Math.random(); // e.g., 1.3, 1.7, etc.

            if (pulseRef.current) {
                pulseRef.current.style.transform = `scale(${scale})`;
                pulseRef.current.style.opacity = `${scale}`;
            }

            const randomInterval = 150;

            pulseTimeout = setTimeout(randomizePulsation, randomInterval);
        };

        randomizePulsation();

        return () => {
            pulseTimeout && clearTimeout(pulseTimeout);
        };
    }, [pulseRef]);

    if (!initialzed) {
        return (
            <div data-testid="active-voice-recognition-loading" className='flex flex col min-h-[100px] justify-center items-center'>
                <Loading />
            </div>
        )
    }

    return (
        <div data-testid="active-voice-recognition-container" className="flex flex-col items-center gap-10">
            <h3 data-testid="active-voice-recognition-listening" className="text-center text-2xl lg:text-3xl">Listening...</h3>
            {transcript && <div data-testid="active-voice-recognition-transcript" className="text-center text-xl lg:text-2xl">{transcript}</div>}
            <div className="mic-icon-wrapper">
                {showPulse ? (
                    <div ref={pulseRef} className="h-20 w-20 bg-mint-400 rounded-full flex justify-center items-center transition-all" />
                ) : (
                    <div className="h-32 w-32 rounded-full animation-pulse" />
                )}
                <img width={148} className="w-[148px] h-[148px]" src={MicLargeIcon} alt="mic-icon" />
            </div>
        </div>
    );
}

export default ActiveVoiceRecognition;